import { getCompanyName } from './Amalgam';
import { isValidNumber, RESELLER_TABS } from './Utils';
import requestLib, { authHttpRequest } from './Request';
import { firebaseLogin, firebaseLogout, getAppAuth } from './Firebase';
import * as queryString from './QueryString';
import {
  doesNativeConnectorExist,
  nativeOnLogout,
  nativePostLoginData,
  nativeSendAnalyticsEvent,
  nativeChatOnWhatsApp
} from './NativeConnector';
import { fetchResellerProfile } from './ResellerProfile';
import { getGroupRouteSlugs } from './GroupCatalogueAmalgam';
import { downloadPictures } from './Download';

let getResellerLoginPromise = null;
let getResellerCustomersPromise = null;
let updateResellerCustomersPromise = null;
let otpAuth = false;

export const CUSTOMER_ROW_HEIGHT = 130;

const CUSTOMER_PAGE_DATA = {
  lastCustomerId: undefined,
  lastDateUpdated: undefined,
  totalCustomers: -1,
  nextPageExists: true,
  prevSearchTerm: null
};

export const clearSavedCustomerData = ({ searchTerm } = {}) => {
  CUSTOMER_PAGE_DATA.lastCustomerId = undefined;
  CUSTOMER_PAGE_DATA.lastDateUpdated = undefined;
  CUSTOMER_PAGE_DATA.totalCustomers = -1;
  CUSTOMER_PAGE_DATA.nextPageExists = true;
  CUSTOMER_PAGE_DATA.prevSearchTerm = searchTerm || null;
};

export const sendMessageOnWhatsapp = ({ message = '', phone }) => {
  if (doesNativeConnectorExist()) {
    nativeChatOnWhatsApp(message, phone);
  }
};

export const isResellerUserLoggedIn = () =>
  new Promise((resolve) => {
    const unsubscribe = getAppAuth().onAuthStateChanged((user) => {
      if (user) {
        resolve(true);
      } else {
        resolve(false);
      }

      // Ensure the subscription is canceled with time for the listener to complete its execution
      setTimeout(() => {
        unsubscribe();
      }, 0);
    });
  });

export const setOtpAuthValue = (otpAuthValue) => {
  otpAuth = typeof otpAuthValue === 'boolean' ? otpAuthValue : false;
};

export const isResellerLoginRequired = () =>
  new Promise((resolve) => {
    if (!otpAuth) {
      resolve(false);
      return;
    }

    isResellerUserLoggedIn().then((loggedIn) => resolve(!loggedIn));
  });

export const parseResellerTabIndex = ({ tabIndex }) =>
  isValidNumber(tabIndex) ? Number(tabIndex) : RESELLER_TABS.CATALOGUE;

const createCustomTokenFromPhone = ({ phone }) =>
  requestLib({
    method: 'POST',
    url: '/api/reseller/custom-token',
    params: { phone }
  });

export const loginResellerUser = ({ phone }) => {
  if (getResellerLoginPromise) {
    return getResellerLoginPromise;
  }

  getResellerLoginPromise = createCustomTokenFromPhone({ phone })
    .then(({ token }) => firebaseLogin({ token }))
    .then(({ credential, displayName, photoURL }) => {
      const { companySlug, randomSlug } = getGroupRouteSlugs();
      return requestLib({
        method: 'POST',
        url: '/api/reseller/login',
        params: {
          token: credential.idToken,
          name: displayName,
          photoURL,
          companySlug,
          randomSlug,
          phone
        }
      });
    })
    .then((authData) => {
      getResellerLoginPromise = null;
      if (doesNativeConnectorExist()) {
        nativePostLoginData({ ...authData, phone });
      }
      sendAnalytics({ name: 'login_signup_done' });
      fetchResellerProfile();
      return authData;
    })
    .catch((loginErr) => {
      try {
        console.error('LOGIN ERROR', JSON.stringify(loginErr));
      } catch (error) {
        console.error('LOGIN stringify ERROR', error);
      }
      firebaseLogout()
        .then(() => {
          getResellerLoginPromise = null;
          // eslint-disable-next-line prefer-promise-reject-errors
          return Promise.reject(loginErr);
        })
        .catch((logOutError) => {
          console.error('LOGIN ERROR LOGOUT ERROR', logOutError);
          getResellerLoginPromise = null;
          // eslint-disable-next-line prefer-promise-reject-errors
          return Promise.reject(loginErr);
        });
    });

  return getResellerLoginPromise;
};

export const createWhatsAppLoginText = ({ routeTo, companySlug, randomSlug }) => {
  const companyName = getCompanyName();
  return `Sign me into ${companyName} web account to access ${routeTo}. CS ${companySlug} and RS ${randomSlug}`;
};

const setResellerCustomersPaginationData = ({
  nextCustomerId,
  nextDateUpdated,
  totalCustomers
} = {}) => {
  if (typeof totalCustomers === 'number') {
    CUSTOMER_PAGE_DATA.totalCustomers = totalCustomers;
  }
  CUSTOMER_PAGE_DATA.lastDateUpdated = nextDateUpdated;
  CUSTOMER_PAGE_DATA.lastCustomerId = nextCustomerId;

  if (!(nextDateUpdated && nextCustomerId)) {
    CUSTOMER_PAGE_DATA.nextPageExists = false;
  }
};

export const getPaginatedResellerCustomers = ({ searchTerm }) => {
  if (getResellerCustomersPromise) {
    return getResellerCustomersPromise;
  }

  if (typeof searchTerm !== 'string') {
    searchTerm = '';
  }

  searchTerm = searchTerm.trim();

  const queryObject = {
    queryValue: searchTerm
  };

  if (CUSTOMER_PAGE_DATA.prevSearchTerm === searchTerm) {
    // Paginated query recieved, pick saved values
    if (CUSTOMER_PAGE_DATA.lastCustomerId) {
      queryObject.lastCustomerId = CUSTOMER_PAGE_DATA.lastCustomerId;
    }
    if (CUSTOMER_PAGE_DATA.lastDateUpdated) {
      queryObject.lastDateUpdated = CUSTOMER_PAGE_DATA.lastDateUpdated;
    }
  } else {
    // search term changed, remove old saved data
    clearSavedCustomerData();
  }

  CUSTOMER_PAGE_DATA.prevSearchTerm = searchTerm;

  let query = '';
  if (Object.keys(queryObject).length > 0) {
    query = `?${queryString.stringify(queryObject)}`;
  }

  getResellerCustomersPromise = new Promise((resolve, reject) => {
    authHttpRequest({
      method: 'GET',
      url: `/api/reseller/customers${query}`
    })
      .then((customerData) => {
        resolve(customerData.customersList);
        getResellerCustomersPromise = null;
        setResellerCustomersPaginationData(customerData);
      })
      .catch(() => {
        // eslint-disable-next-line prefer-promise-reject-errors
        reject();
        getResellerCustomersPromise = null;
      });
  });

  return getResellerCustomersPromise;
};

export const getCustomerBillingDataFromCustomerData = (data) => ({
  id: data.customerId || null,
  name: data.customerName,
  phone: data.customerPhone,
  email: data.customerEmail
});

export const getCustomerLinkedResellerDataFromCustomerData = (data) => ({
  linkedResellerName: data.linkedResellerName,
  linkedResellerPhone: data.linkedResellerPhone
});

export const getCustomerShippingDataFromCustomerData = (data) => data.shippingAddressList[0] || {};

export const createCustomerPhoneString = ({ phone, callingCode }) => {
  if (!phone || !callingCode) {
    return '';
  }

  return `${callingCode}${phone}`.trim();
};

export const upsertCustomerData = (
  customerData,
  resellerData,
  shippingAddress,
  { validateMandatory = false } = {}
) => {
  if (getResellerCustomersPromise) {
    return getResellerCustomersPromise;
  }

  customerData = customerData || {};
  resellerData = resellerData || {};

  updateResellerCustomersPromise = new Promise((resolve, reject) => {
    authHttpRequest({
      method: 'POST',
      url: '/api/reseller/customers',
      params: {
        customerId: customerData.id,
        customerName: customerData.name,
        customerPhone: createCustomerPhoneString({
          phone: customerData.phone,
          callingCode: customerData.callingCode
        }),
        customerEmail: customerData.email,
        linkedResellerName: resellerData.linkedResellerName || undefined,
        linkedResellerPhone: resellerData.linkedResellerPhone || undefined,
        shippingAddress,
        validateMandatory
      }
    })
      .then((response) => {
        resolve(response.customerId);
        updateResellerCustomersPromise = null;
      })
      .catch((err) => {
        reject(err);
        updateResellerCustomersPromise = null;
      });
  });

  return updateResellerCustomersPromise;
};

export const sendAnalytics = ({ name, props }) => {
  if (doesNativeConnectorExist()) {
    nativeSendAnalyticsEvent({ name, props });
  }
};

export const downloadPicturesForReseller = ({ productIds, analyticsName }) => {
  downloadPictures({ productIds });
  sendAnalytics({ name: analyticsName });
};

export const onLogout = () => {
  sendAnalytics({ name: 'logout_done' });
  if (doesNativeConnectorExist()) {
    nativeOnLogout();
  }
};
